import { create } from "zustand"
import { persist } from "zustand/middleware"

const MAX_HEARTS = 5

export interface CardResult {
  cardId: string
  correct: boolean
  responseTimeMs: number
}

interface LessonSession {
  topicId: string
  lessonId: string
  currentIndex: number
  results: CardResult[]
  hearts: number
}

interface LessonStore {
  session: LessonSession | null

  /** Initialize a fresh session for a topic */
  startSession: (topicId: string, lessonId: string) => void

  /** Resume a session from DB-backed progress */
  resumeSession: (topicId: string, lessonId: string, answeredCount: number, wrongCount: number, results: CardResult[]) => void

  /** Record an answer and update hearts */
  recordAnswer: (result: CardResult) => void

  /** Move to the next card */
  advanceCard: () => void

  /** Clear the session (on complete or wipe) */
  clearSession: () => void
}

export const useLessonStore = create<LessonStore>()(
  persist(
    (set) => ({
      session: null,

      startSession: (topicId, lessonId) =>
        set({
          session: {
            topicId,
            lessonId,
            currentIndex: 0,
            results: [],
            hearts: MAX_HEARTS,
          },
        }),

      resumeSession: (topicId, lessonId, answeredCount, wrongCount, results) =>
        set({
          session: {
            topicId,
            lessonId,
            currentIndex: answeredCount,
            results,
            hearts: Math.max(0, MAX_HEARTS - wrongCount),
          },
        }),

      recordAnswer: (result) =>
        set((state) => {
          if (!state.session) return state
          const newHearts = result.correct
            ? state.session.hearts
            : state.session.hearts - 1
          return {
            session: {
              ...state.session,
              results: [...state.session.results, result],
              hearts: newHearts,
            },
          }
        }),

      advanceCard: () =>
        set((state) => {
          if (!state.session) return state
          return {
            session: {
              ...state.session,
              currentIndex: state.session.currentIndex + 1,
            },
          }
        }),

      clearSession: () => set({ session: null }),
    }),
    {
      name: "apollo:lesson-session",
    }
  )
)
